import React, { useCallback, useEffect, useRef, useState } from 'react';
import { auth } from '../../../../auth/clientAppAuth';
import { persistClientAvatarUrl } from '../../../auth/utils/storefrontClientSync';
import { deleteStoredFile, uploadFile } from '../../../../api/files';

/**
 * Photo upload for storefront account — same path as client-portal ClientAvatarUpload.
 */
const StorefrontProfileAvatar = ({ clientUser, onAvatarSaved, onRefresh }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const clearMsg = useCallback(() => setMessage({ type: '', text: '' }), []);
  const showErr = (text) => setMessage({ type: 'error', text });
  const showOk = (text) => setMessage({ type: 'success', text });

  const hasPhoto = Boolean(clientUser?.avatarUrl);
  const resolveUserId = () => auth.currentUser?.uid || clientUser?.id;

  const storagePath = clientUser?.companyId && resolveUserId()
    ? `client-portal/${clientUser.companyId}/avatars/${resolveUserId()}`
    : null;

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  const handleAvatarClick = () => {
    if (busy || !storagePath) return;
    if (!hasPhoto) {
      clearMsg();
      fileRef.current?.click();
      return;
    }
    setMenuOpen((open) => !open);
  };

  const openFilePicker = () => {
    setMenuOpen(false);
    clearMsg();
    window.setTimeout(() => fileRef.current?.click(), 0);
  };

  const handleRemovePhoto = async () => {
    const userId = resolveUserId();
    if (!storagePath || !clientUser?.companyId || !userId) return;
    setMenuOpen(false);

    const ok = window.confirm('Remove your profile photo?');
    if (!ok) return;

    setBusy(true);
    clearMsg();

    try {
      await deleteStoredFile(storagePath, { companyId: clientUser.companyId });

      persistClientAvatarUrl(clientUser.companyId, userId, null, {
        onSaved: onAvatarSaved,
        onRefresh,
      });

      showOk('Photo removed.');
    } catch (error) {
      console.error(error);
      showErr(error.message || 'Could not remove photo.');
    } finally {
      setBusy(false);
    }
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    const userId = resolveUserId();
    if (!file || !storagePath || !clientUser?.companyId || !userId) return;

    setBusy(true);
    clearMsg();

    try {
      const stored = await uploadFile(file, storagePath, { companyId: clientUser.companyId });
      const downloadURL = stored.url;

      persistClientAvatarUrl(clientUser.companyId, userId, downloadURL, {
        onSaved: onAvatarSaved,
        onRefresh,
      });

      setMenuOpen(false);
      showOk('Profile photo updated.');
    } catch (error) {
      console.error(error);
      showErr(error.message || 'Could not update photo.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`sf-profile-avatar${menuOpen ? ' sf-profile-avatar--menu-open' : ''}`}>
      <input ref={fileRef} type="file" accept="image/*" className="sf-profile-avatar__input" onChange={handleFile} />

      {menuOpen ? (
        <div className="sf-profile-avatar__backdrop" aria-hidden onClick={() => setMenuOpen(false)} />
      ) : null}

      <button
        type="button"
        className={`sf-profile-avatar__preview${!hasPhoto ? ' sf-profile-avatar__preview--empty' : ''}${busy ? ' sf-profile-avatar__preview--busy' : ''}`}
        onClick={handleAvatarClick}
        disabled={busy || !storagePath}
        aria-busy={busy}
        aria-expanded={hasPhoto ? menuOpen : undefined}
        aria-haspopup={hasPhoto ? 'dialog' : undefined}
        aria-label={hasPhoto ? 'Profile photo' : 'Add profile photo'}
      >
        {hasPhoto ? (
          <img src={clientUser.avatarUrl} alt="" className="sf-profile-avatar__img" />
        ) : (
          <span className="sf-profile-avatar__empty">
            <span className="sf-profile-avatar__empty-plus" aria-hidden>
              +
            </span>
            <span className="sf-profile-avatar__empty-label">Add photo</span>
          </span>
        )}
        {busy ? (
          <span className="sf-profile-avatar__busy" aria-hidden>
            <span className="sf-profile-avatar__busy-spinner" />
          </span>
        ) : null}
      </button>

      {menuOpen && hasPhoto ? (
        <div
          className="sf-profile-avatar__menu"
          role="dialog"
          aria-modal="true"
          aria-label="Change or remove photo"
        >
          <button type="button" className="sf-profile-avatar__menu-option" onClick={openFilePicker}>
            Change photo
          </button>
          <button type="button" className="sf-profile-avatar__menu-option sf-profile-avatar__menu-option--danger" onClick={handleRemovePhoto}>
            Remove photo
          </button>
        </div>
      ) : null}

      {message.text ? (
        <p className={`sf-profile-avatar__msg sf-profile-avatar__msg--${message.type}`} role={message.type === 'error' ? 'alert' : 'status'}>
          {message.text}
        </p>
      ) : null}
    </div>
  );
};

export default StorefrontProfileAvatar;
